import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Col, Row } from 'react-bootstrap'
import { Box, Button, Text, useErrorModal } from '~/design_system'
import { Msg, useMsg } from '~/common/localizationUtils'

const MAX_SIZE = 5242880
const ACCEPTED_FORMATS =
  'image/jpeg, image/png, application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document'

const FileUploadZone = ({
  onFileDrop,
  onFilesDrop = null, // Added for consumers who want a list of files instead of the callback invoked for every file
  maxSize = MAX_SIZE,
  acceptedFormats = ACCEPTED_FORMATS,
  supportedFormatText,
  maxSizeText,
  isLoading = false,
  id
}: {
  onFileDrop: (acceptedFiles: File) => void
  onFilesDrop?: ((files: File[]) => void) | null
  maxSize?: number
  acceptedFormats?: string
  supportedFormatText?: string
  maxSizeText?: string
  isLoading?: boolean
  id?: string
}) => {
  const errorModal = useErrorModal()
  const msg = useMsg()
  const onDrop = useCallback(
    /*
        eslint-disable-next-line react-hooks/preserve-manual-memoization --

        ⚠️ HEY YOU, DEV! Yes, YOU! Don't you look away...I know you are reading this. This is your problem now.
        Fix the below while you are working in here. It's simple, just delete this comment, and you'll see the lint error.
        There's even a suggestion on how you might fix it. These particular lint errors are serious issues that we
        couldn't feasibly fix across the whole codebase, but they need fixed. Your help is appreciated! 🥳

        Issue: `onFilesDrop`, `errorModal`, `msg`, `supportedFormatText`, `maxSizeText` are used but only [onFileDrop] is in deps.
        Possible fix approach:
          // Option 1: Add all used dependencies:
          const onDrop = useCallback(
            (acceptedFiles, rejectedFiles) => { ... },
            [onFileDrop, onFilesDrop, errorModal, msg, supportedFormatText, maxSizeText]
          )

          // Option 2: Remove useCallback entirely - the memoization is not needed (verify performance is acceptable)
    */
    (acceptedFiles: File[], rejectedFiles: File[]) => {
      if (onFilesDrop) {
        onFilesDrop(acceptedFiles)
      } else {
        acceptedFiles.forEach(file => {
          onFileDrop(file)
        })
      }
      rejectedFiles.forEach(file => {
        errorModal.showModal({
          title: msg('file_security.invalid_file.error.title'),
          message: msg('file_upload_zone.unexpected_error', {
            description:
              'File Invalid: {fileName}. Must be of type {supportedFormatText} and be smaller than {maxSizeText}.',
            filename: file.name,
            supportedFormatText: supportedFormatText ?? msg('file_upload_zone.supported_types'),
            maxSizeText: maxSizeText ?? msg('common.ui.max_file_size_mb', { num: '5' })
          })
        })
      })
    },
    [onFileDrop]
  )

  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptedFormats,
    maxSize,
    onDrop
  })

  return (
    <Box bg="system.color.bg.alt.soft" textAlign="center" padding={4}>
      <div {...getRootProps()}>
        <input {...getInputProps({ id })} data-testid="file-upload-input" />
        <Text textStyle="bodySemi">
          <Msg id="common.ui.drag_and_drop_file" description="Drag and Drop File" />
        </Text>
        <Text textStyle="body">
          <Msg
            id="common.ui.only_supported_file_formats_notice"
            supportedFormatText={supportedFormatText ?? msg('file_upload_zone.supported_types')}
            description="Only {supportedFormatText} file formats supported"
          />
        </Text>
        <Text textStyle="subtext">
          <Msg
            id="common.ui.max_file_size.notice"
            maxSizeText={maxSizeText ?? msg('common.ui.max_file_size_mb', { num: '5' })}
            description="Maximum file size: <maxSizeText>"
          />
        </Text>
        <Row>
          <Col md={4} mdOffset={4}>
            <Button disabled={isLoading} variant="primary" size="sm" marginTop={2}>
              <Msg
                id="common.ui.select_file"
                maxSizeText={maxSizeText ?? msg('common.ui.max_file_size_mb', { num: '5' })}
                description="Select File"
              />
            </Button>
          </Col>
        </Row>
      </div>
    </Box>
  )
}

export default FileUploadZone
